// src/pages/AdminDashboard.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Divider,
  List,
  CircularProgress,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  People,
  MenuBook,
  School,
  TrendingUp,
  Add,
  Edit,
  Delete,
  Visibility,
  ExpandMore,
  Close,
  CheckCircle,
  Cancel,
  Search,
  Assignment,
  Quiz,
  PlayCircle,
  Description,
  Save,
  Refresh,
  Person,
  Email,
  Badge,
  Work,
  AdminPanelSettings as AdminIcon,
  Category,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { useAmbito } from '../context/AmbitoContext';
import PreguntasEditor from '../components/admin/PreguntasEditor';
import RichTextEditor from '../components/common/RichTextEditor';
import ImagenUploader from '../components/common/ImagenUploader';
import { toHtml } from '../utils/markdown';
import useCourseStore from '../context/courseStore';
import { meService, adminService, cursoService, entregaService, categoriaAdminService } from '../services/api';
import { getAllContents, contentTypes, categories } from '../data/courses';

// ============ COMPONENTE DE ESTADÍSTICAS ============
// REESCRITO — usa adminService.getStats() real (GET /admin/stats),
// que ya contaba con datos reales de toda la red en el backend.
function StatsCards() {
  const [stats, setStats] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    adminService.getStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setCargando(false));
  }, []);

  if (cargando) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;
  }
  if (!stats) return null;

  const cards = [
    { label: 'Total Usuarios', value: stats.totalUsers, icon: People, color: '#e94560' },
    { label: 'Instituciones activas', value: stats.totalInstitutions, icon: School, color: '#9c27b0' },
    { label: 'Cursos publicados', value: stats.totalCourses, icon: MenuBook, color: '#3b82f6' },
    { label: 'Matrículas activas', value: stats.totalEnrollments, icon: TrendingUp, color: '#00d9a5' },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {cards.map((stat, i) => (
        <Grid item xs={6} md={3} key={i}>
          <Card>
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                <Typography variant="h4" fontWeight={700}>{stat.value}</Typography>
              </Box>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                bgcolor: `${stat.color}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <stat.icon sx={{ color: stat.color }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

// ============ TAB ESTUDIANTES ============
// REESCRITO — usa adminService.getUsuarios({role:'student'}) real
// (GET /admin/usuarios?rol=Estudiante), red completa, paginado.
//
// SIMPLIFICACIÓN HONESTA: el mock tenía un dialog de detalle con
// desglose de quizzes/actividades por curso, por estudiante. No existe
// un endpoint admin-wide para "ver el progreso de cualquier estudiante
// en cualquier curso sin conocer su matriculaId" -- se reemplaza por
// un detalle simple con los datos reales disponibles (contacto,
// ubicación). El desglose curso-por-curso queda pendiente de un
// endpoint nuevo si se necesita.
function StudentsTab() {
  const [estudiantes, setEstudiantes] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    setCargando(true);
    adminService.getUsuarios({ role: 'student', limit: 100 })
      .then((r) => { setEstudiantes(r.data); setTotal(r.total); })
      .catch((e) => setError(e?.message || 'No se pudieron cargar los estudiantes.'))
      .finally(() => setCargando(false));
  }, []);

  const filteredStudents = useMemo(() => {
    return estudiantes.filter((s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [estudiantes, searchQuery]);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar estudiante..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        <Typography color="text.secondary">
          {filteredStudents.length} de {total} estudiante(s)
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Estudiante</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Ubicación</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStudents.map((student) => (
                <TableRow key={student.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={student.avatar}>{student.name?.[0]}</Avatar>
                      <Typography fontWeight={500}>{student.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{student.email}</TableCell>
                  <TableCell>{[student.municipality, student.department].filter(Boolean).join(', ') || '—'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalle">
                      <IconButton onClick={() => setSelectedStudent(student)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredStudents.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No hay estudiantes que coincidan</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!selectedStudent} onClose={() => setSelectedStudent(null)} maxWidth="sm" fullWidth>
        {selectedStudent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={selectedStudent.avatar} sx={{ width: 56, height: 56 }}>{selectedStudent.name?.[0]}</Avatar>
                <Box>
                  <Typography variant="h6">{selectedStudent.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedStudent.email}</Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary">
                Ubicación: {[selectedStudent.municipality, selectedStudent.department].filter(Boolean).join(', ') || 'No registrada'}
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                El detalle de cursos y progreso por estudiante requiere consultarlo desde
                "Estudiantes" dentro del curso específico (panel de instructor).
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedStudent(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

// ============ TAB INSTRUCTORES ============
// REESCRITO — la identidad vive en Ecolab, EducaT no puede "crear
// instructores con contraseña" (así lo hacía el mock). Lo que sí
// puede hacer un Administrador global es: ascender a un usuario
// existente de la red a rol Instructor (adminService.cambiarRol,
// PUT /admin/usuarios/rol, ya protegido por RequireEducaTGlobalAdminFilter)
// y reasignar qué instructor dicta cada curso (cursoService.update).
function InstructorsTab() {
  const nombreProducto = useTheme().productoInfo?.nombre || 'EducaT';
  const [instructors, setInstructors] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [promoteDialog, setPromoteDialog] = useState(false);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteResult, setPromoteResult] = useState(null);
  const [promoviendo, setPromoviendo] = useState(false);

  const cargarInstructores = () => {
    setCargando(true);
    adminService.getUsuarios({ role: 'instructor', limit: 100 })
      .then((r) => { setInstructors(r.data); setTotal(r.total); })
      .catch((e) => setError(e?.message || 'No se pudieron cargar los instructores.'))
      .finally(() => setCargando(false));
  };

  useEffect(cargarInstructores, []);

  const filteredInstructors = useMemo(() => {
    return instructors.filter((i) =>
      i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [instructors, searchQuery]);

  const handlePromote = async () => {
    if (!promoteEmail.trim()) return;
    setPromoviendo(true);
    setPromoteResult(null);
    try {
      // adminService.getUsuarios no filtra por email -- se busca dentro
      // de estudiantes ya cargados por coincidencia exacta de correo.
      // Si no está en la lista cargada (paginada a 100), no hay forma
      // de buscarlo sin un endpoint de búsqueda por email -- se informa.
      const candidato = instructors.concat(
        (await adminService.getUsuarios({ role: 'student', limit: 200 })).data
      ).find((u) => u.email.toLowerCase() === promoteEmail.trim().toLowerCase());

      if (!candidato) {
        setPromoteResult({ ok: false, msg: 'No se encontró ningún usuario de la red con ese correo (o está fuera de las primeras 200 filas cargadas).' });
        return;
      }

      await adminService.cambiarRol(candidato.id, 'Instructor');
      setPromoteResult({ ok: true, msg: `${candidato.name} ahora es Instructor.` });
      setPromoteEmail('');
      cargarInstructores();
    } catch (e) {
      setPromoteResult({ ok: false, msg: e?.message || 'No se pudo ascender al usuario.' });
    } finally {
      setPromoviendo(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Buscar instructor..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
          <Typography color="text.secondary">{filteredInstructors.length} de {total} instructor(es)</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setPromoteResult(null); setPromoteDialog(true); }}>
          Ascender a Instructor
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Instructor</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Ubicación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInstructors.length > 0 ? filteredInstructors.map((instructor) => (
                <TableRow key={instructor.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={instructor.avatar}>{instructor.name?.[0]}</Avatar>
                      <Typography fontWeight={500}>{instructor.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{instructor.email}</TableCell>
                  <TableCell>{[instructor.municipality, instructor.department].filter(Boolean).join(', ') || '—'}</TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No hay instructores todavía</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={promoteDialog} onClose={() => setPromoteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ascender usuario a Instructor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            El usuario debe ya existir en la red (haber iniciado sesión al menos una vez).
            {nombreProducto} no crea cuentas nuevas -- la identidad vive en Ecolab.
          </Typography>
          <TextField
            fullWidth label="Email del usuario" value={promoteEmail}
            onChange={(e) => setPromoteEmail(e.target.value)} sx={{ mb: 2 }}
          />
          {promoteResult && (
            <Alert severity={promoteResult.ok ? 'success' : 'error'}>{promoteResult.msg}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialog(false)}>Cerrar</Button>
          <Button variant="contained" onClick={handlePromote} disabled={!promoteEmail.trim() || promoviendo}>
            {promoviendo ? 'Ascendiendo…' : 'Ascender'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
// ============ TAB CURSOS ============
//
// El selector de ambito (Red / Institucion X / ...) vive en el
// AmbitoProvider global y en el menu del perfil del Header. Aqui
// solo CONSUMIMOS el ambito actual y recargamos los cursos cuando
// cambia.
function CoursesTab() {
  const { getAllCourses, fetchCourses, fetchCourseDetail, createCourse, updateCourse, deleteCourse, addUnit, deleteUnit, addContent, deleteContent } = useCourseStore();
  const { ambitoId, ambitoNombre, esRed, institucionIdActual, opciones: opcionesAmbito } = useAmbito();

  const [cargando, setCargando]           = useState(true);
  const [errorGuardado, setErrorGuardado] = useState(null);

  // Categorías del backend (reemplazan al mock data/courses.js). Se
  // recargan si el admin edita categorías en el tab correspondiente
  // y vuelve aqui — a traves de un reload normal.
  const [categoriasBackend, setCategoriasBackend] = useState([]);
  useEffect(() => {
    const { catalogoService } = require('../services/api');
    catalogoService.getCategorias().then(setCategoriasBackend).catch(() => setCategoriasBackend([]));
  }, []);
  // Nota: el service devuelve { id, name, icon, color }. Los cursos
  // guardan categoriaId (int) referenciando categorias.id. Pero el
  // mock antiguo usa id string ('programming', 'data', ...). Durante
  // la transicion toleramos ambos.
  const findCat = useCallback((courseCat) => {
    if (courseCat == null) return null;
    return categoriasBackend.find((c) => c.id === courseCat)
        || categoriasBackend.find((c) => String(c.id) === String(courseCat))
        || categories.find((c) => c.id === courseCat)
        || null;
  }, [categoriasBackend]);

  // Cada vez que cambia el ambito activo: recargar cursos filtrados.
  useEffect(() => {
    if (!ambitoId) { setCargando(false); return; }
    (async () => {
      setCargando(true);
      try {
        const filtro = esRed
          ? { isGlobal: true }                   // cursos del tenant / red
          : { institutionId: institucionIdActual }; // cursos de la institucion seleccionada
        await fetchCourses(filtro);
      } catch (e) {
        console.warn('[CoursesTab] error cargando cursos:', e.message);
      } finally {
        setCargando(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambitoId]);

  // institutionId que viajara al backend al crear/editar: null para la red.
  const institutionIdParaGuardar = institucionIdActual;

  const [editingCourse, setEditingCourse] = useState(null);
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const FORM_INICIAL_CURSO = {
    title: '',
    subtitle: '',
    description: '',
    category: '',
    level: 'Basico',
    duration: '8 semanas',
    credits: 3,
    price: 0,
    image: '',          // URL firmada actual para previsualizar
    imageGsUri: '',     // gs:// que se mandara al update del curso
  };
  const [uploadingCourseImage, setUploadingCourseImage] = useState(false);
  const [courseForm, setCourseForm] = useState(FORM_INICIAL_CURSO);
  const [guardando, setGuardando] = useState(false);
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [unitForm, setUnitForm] = useState({ title: '', description: '' });
  const [selectedCourseForUnit, setSelectedCourseForUnit] = useState(null);
  
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [contentForm, setContentForm] = useState({ 
    title: '', 
    description: '', 
    type: 'material', 
    points: 0 
  });
  const [selectedUnitForContent, setSelectedUnitForContent] = useState(null);

  // ── Edicion de unidad existente ───────────────────────────────
  const [editingUnit, setEditingUnit] = useState(null);          // { courseId, unitId, originalOrder }
  const [editUnitForm, setEditUnitForm] = useState({ title: '', description: '' });
  const [showEditUnitDialog, setShowEditUnitDialog] = useState(false);
  const [guardandoUnidad, setGuardandoUnidad] = useState(false);

  // ── Edicion de contenido existente ────────────────────────────
  const [editingContent, setEditingContent] = useState(null);    // { courseId, unitId, contentId, type, order }
  const [editContentForm, setEditContentForm] = useState({
    title: '', description: '', bodyText: '', resourceUrl: '',
    maxPoints: 0, durationMinutes: 0, minScore: 70, maxAttempts: 3,
    requiresPrevious: true,
  });
  const [showEditContentDialog, setShowEditContentDialog] = useState(false);
  const [guardandoContenido, setGuardandoContenido] = useState(false);
  const [errorEditando, setErrorEditando] = useState(null);

  const courses = getAllCourses();

  // ── Handlers de edicion ───────────────────────────────────────
  const handleStartEditUnit = (course, unit) => {
    setEditingUnit({ courseId: course.id, unitId: unit.id, originalOrder: unit.order });
    setEditUnitForm({ title: unit.title || '', description: unit.description || '' });
    setErrorEditando(null);
    setShowEditUnitDialog(true);
  };

  const handleSaveEditUnit = async () => {
    if (!editingUnit) return;
    setGuardandoUnidad(true);
    setErrorEditando(null);
    const payload = {
      title: editUnitForm.title,
      description: editUnitForm.description,
      order: editingUnit.originalOrder || 1,
    };
    const r = await useCourseStore.getState()
      .updateUnit(editingUnit.courseId, editingUnit.unitId, payload)
      .catch((e) => ({ success: false, error: e.message }));
    setGuardandoUnidad(false);
    if (!r.success) { setErrorEditando(r.error || 'No se pudo guardar.'); return; }
    setShowEditUnitDialog(false);
    setEditingUnit(null);
  };

  const handleStartEditContent = (course, unit, content) => {
    setEditingContent({
      courseId: course.id, unitId: unit.id, contentId: content.id,
      type: content.type, order: content.order,
    });
    setEditContentForm({
      title: content.title || '',
      description: content.description || '',
      // Si en BD esta en Markdown (de los seeds o imports), lo convertimos
      // a HTML para que el editor WYSIWYG lo pinte correctamente. Si ya
      // esta en HTML, pasa tal cual.
      bodyText: toHtml(content.body || ''),
      resourceUrl: content.resourceUrl || '',
      maxPoints: content.maxPoints ?? 0,
      durationMinutes: content.durationMinutes ?? 0,
      minScore: content.minScore ?? 70,
      maxAttempts: content.maxAttempts ?? 3,
      requiresPrevious: content.requiresPrevious !== false,
    });
    setErrorEditando(null);
    setShowEditContentDialog(true);
  };

  const handleSaveEditContent = async () => {
    if (!editingContent) return;
    setGuardandoContenido(true);
    setErrorEditando(null);
    const payload = {
      type: editingContent.type,
      title: editContentForm.title,
      description: editContentForm.description,
      bodyText: editContentForm.bodyText,
      resourceUrl: editContentForm.resourceUrl,
      maxPoints: Number(editContentForm.maxPoints) || 0,
      durationMinutes: Number(editContentForm.durationMinutes) || 0,
      minScore: Number(editContentForm.minScore) || 0,
      maxAttempts: Number(editContentForm.maxAttempts) || 0,
      requiresPrevious: !!editContentForm.requiresPrevious,
      order: editingContent.order || 1,
    };
    const r = await useCourseStore.getState()
      .updateContent(editingContent.courseId, editingContent.unitId, editingContent.contentId, payload)
      .catch((e) => ({ success: false, error: e.message }));
    setGuardandoContenido(false);
    if (!r.success) { setErrorEditando(r.error || 'No se pudo guardar.'); return; }
    setShowEditContentDialog(false);
    setEditingContent(null);
  };

  const handleSaveCourse = async () => {
    setErrorGuardado(null);
    setGuardando(true);
    const datos = {
      ...courseForm,
      institutionId: institutionIdParaGuardar || undefined,
      // Si estamos en el ambito "Red", marcar el curso como global
      isGlobal: esRed,
    };
    const result = editingCourse
      ? await updateCourse(editingCourse.id, datos)
      : await createCourse(datos);
    setGuardando(false);
    if (!result.success) {
      setErrorGuardado(result.error || 'No se pudo guardar el curso.');
      return;
    }
    setShowCourseDialog(false);
    setEditingCourse(null);
    setCourseForm(FORM_INICIAL_CURSO);
  };

  const handleEditCourse = async (listado) => {
    // El listado no trae descripcion ni categoryId confiable: se abre el
    // modal con el detalle real, si no el Guardar mandaria descripcion
    // vacia y la borraria.
    let course = listado;
    try {
      course = (await cursoService.getById(listado.id)) || listado;
    } catch (e) {
      console.warn('[CoursesTab] no se pudo cargar el detalle del curso:', e.message);
    }
    setEditingCourse(course);
    setErrorGuardado(null);
    setCourseForm({
      title: course.title,
      subtitle: course.subtitle || '',
      description: course.description || '',
      // El select usa el id de categorias (int); course.category es el nombre.
      category: course.categoryId ?? '',
      // Valores del enum NivelCurso del backend: Basico | Intermedio | Avanzado.
      level: course.level || 'Basico',
      duration: course.duration,
      credits: course.credits,
      price: course.price,
      image: course.image || '',
      imageGsUri: '',
    });
    setShowCourseDialog(true);
  };

  // Sube la imagen hero del curso ya existente. El endpoint tambien
  // actualiza cursos.imagen_url en BD, asi que un refetch posterior
  // ya ve la nueva URL.
  const handleUploadCourseImage = async (file) => {
    if (!editingCourse?.id) {
      setErrorGuardado('Guarda el curso primero y luego sube la imagen.');
      return;
    }
    setUploadingCourseImage(true);
    try {
      const r = await cursoService.subirImagen(editingCourse.id, file);
      setCourseForm((f) => ({
        ...f,
        image: r.signedUrl || r.gsUri || '',
        imageGsUri: r.gsUri || '',
      }));
      // Refrescar lista de cursos para que la card del grid muestre la nueva imagen
      await fetchCourses?.();
    } catch (e) {
      setErrorGuardado(e?.message || 'No se pudo subir la imagen.');
    } finally {
      setUploadingCourseImage(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('¿Estás seguro de eliminar este curso?')) return;
    const result = await deleteCourse(courseId);
    if (!result?.success) {
      alert(result?.error || 'No se pudo eliminar el curso.');
    }
  };

  const handleAddUnit = async () => {
    if (!selectedCourseForUnit || !unitForm.title) return;
    const result = await addUnit(selectedCourseForUnit, unitForm);
    if (!result?.success) {
      alert(result?.error || 'No se pudo agregar la unidad.');
      return;
    }
    setShowUnitDialog(false);
    setUnitForm({ title: '', description: '' });
    setSelectedCourseForUnit(null);
  };

  const handleAddContent = async () => {
    if (!selectedCourseForUnit || !selectedUnitForContent || !contentForm.title) return;
    const result = await addContent(selectedCourseForUnit, selectedUnitForContent, contentForm);
    if (!result?.success) {
      alert(result?.error || 'No se pudo agregar el contenido.');
      return;
    }
    setShowContentDialog(false);
    setContentForm({ title: '', description: '', type: 'material', points: 0 });
  };

  if (cargando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Cabecera: contador + hint de ambito + boton nuevo curso.
          El SELECTOR de ambito (Red / institucion) vive en el menu
          del perfil del Header para que sea accesible desde cualquier
          pagina, no solo este tab. */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6">
            {courses.length} curso(s){ambitoNombre ? ` — ${ambitoNombre}` : ''}
          </Typography>
          {opcionesAmbito.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              Puedes cambiar el ámbito desde el menú de tu perfil (arriba a la derecha).
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setShowCourseDialog(true)}
        >
          Nuevo Curso
        </Button>
      </Box>

      {courses.map(course => {
        const allContents = getAllContents(course);
        const instructor = course.instructor;
        
        return (
          <Accordion
            key={course.id}
            sx={{ mb: 1 }}
            onChange={(e, expanded) => {
              // Los cursos vienen de la LISTA (sin unidades/contenidos
              // anidados) -- al expandir, si todavía no se cargó el
              // detalle completo, se trae una sola vez.
              if (expanded && !course.units) fetchCourseDetail(course.id);
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={600}>{course.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {instructor ? `👨‍🏫 ${instructor.name} • ` : ''}
                    {course.units?.length || 0} unidades • {allContents.length} contenidos • {course.totalEnrollments || 0} estudiantes
                  </Typography>
                </Box>
                <Chip 
                  label={findCat(course.category)?.name || '—'} 
                  size="small" 
                  sx={{ bgcolor: findCat(course.category)?.color, color: 'white' }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button size="small" startIcon={<Edit />} onClick={() => handleEditCourse(course)}>
                  Editar
                </Button>
                <Button 
                  size="small" 
                  startIcon={<Add />} 
                  onClick={() => { setSelectedCourseForUnit(course.id); setShowUnitDialog(true); }}
                >
                  Agregar Unidad
                </Button>
                <Button 
                  size="small" 
                  color="error" 
                  startIcon={<Delete />} 
                  onClick={() => handleDeleteCourse(course.id)}
                >
                  Eliminar
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Unidades del curso */}
              {course.units?.map((unit, ui) => (
                <Box key={unit.id} sx={{ mb: 2, pl: 2, borderLeft: 2, borderColor: 'primary.main' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography fontWeight={500}>
                      Unidad {ui + 1}: {unit.title}
                    </Typography>
                    <Box>
                      <IconButton
                        size="small"
                        title="Editar unidad"
                        onClick={() => handleStartEditUnit(course, unit)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Agregar contenido"
                        onClick={() => {
                          setSelectedCourseForUnit(course.id);
                          setSelectedUnitForContent(unit.id);
                          setShowContentDialog(true);
                        }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title="Eliminar unidad"
                        onClick={async () => {
                          if (!window.confirm(`¿Eliminar la unidad "${unit.title}"? Se desactivarán también sus contenidos.`)) return;
                          const r = await deleteUnit(course.id, unit.id);
                          if (!r.success) alert(r.error);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <List dense>
                    {unit.contents?.map(content => {
                      // Mapeo de tipos del backend (PascalCase) a los iconos locales:
                      // los Icons de contentTypes usan claves lowercase legacy.
                      const normalizedType = String(content.type || '').toLowerCase();
                      const Icon = normalizedType === 'video' ? PlayCircle
                        : (normalizedType === 'quiz' || normalizedType === 'examen' || normalizedType === 'exam') ? Quiz
                        : (normalizedType === 'actividad' || normalizedType === 'activity') ? Assignment
                        : Description;

                      return (
                        <ListItem
                          key={content.id}
                          secondaryAction={
                            <Box>
                              <IconButton
                                size="small"
                                title="Editar"
                                onClick={() => handleStartEditContent(course, unit, content)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                title="Eliminar"
                                onClick={async () => {
                                  if (!window.confirm(`¿Eliminar "${content.title}"?`)) return;
                                  const r = await deleteContent(course.id, unit.id, content.id);
                                  if (!r.success) alert(r.error);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Icon fontSize="small" sx={{ color: contentTypes[normalizedType]?.color || '#666' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={content.title}
                            secondary={`${content.type} ${content.maxPoints > 0 ? `• ${content.maxPoints} pts` : ''}`}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              ))}

              {(!course.units || course.units.length === 0) && (
                <Alert severity="info">Este curso no tiene unidades todavía</Alert>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Dialog para crear/editar curso */}
      <Dialog open={showCourseDialog} onClose={() => { setShowCourseDialog(false); setEditingCourse(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCourse ? 'Editar Curso' : 'Nuevo Curso'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {errorGuardado && <Alert severity="error" onClose={() => setErrorGuardado(null)}>{errorGuardado}</Alert>}
            <Alert severity="info" sx={{ py: 0.5 }}>
              {esRed
                ? <>Este curso se creará como <strong>curso global de la red</strong> (sin institución específica).</>
                : <>Este curso quedará vinculado a <strong>{ambitoNombre}</strong>.</>}
            </Alert>
            <TextField
              label="Título del curso"
              value={courseForm.title}
              onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Subtítulo"
              value={courseForm.subtitle}
              onChange={(e) => setCourseForm({ ...courseForm, subtitle: e.target.value })}
              fullWidth
            />
            <TextField
              label="Descripción"
              value={courseForm.description}
              onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <ImagenUploader
              label="Imagen del curso (hero)"
              preview={courseForm.image || null}
              disabled={!editingCourse}
              uploading={uploadingCourseImage}
              onPick={handleUploadCourseImage}
              hint={editingCourse
                ? 'Formato: PNG, JPG o WEBP. Recomendado 1200×600 px.'
                : 'Guarda el curso primero y vuelve a abrir "Editar" para poder subir la imagen.'}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    value={courseForm.category}
                    label="Categoría"
                    onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                  >
                    {(categoriasBackend.length ? categoriasBackend : categories).map(cat => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Nivel</InputLabel>
                  <Select
                    value={courseForm.level}
                    label="Nivel"
                    onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value })}
                  >
                    <MenuItem value="Basico">Básico</MenuItem>
                    <MenuItem value="Intermedio">Intermedio</MenuItem>
                    <MenuItem value="Avanzado">Avanzado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Duración"
                  value={courseForm.duration}
                  onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Créditos"
                  type="number"
                  value={courseForm.credits}
                  onChange={(e) => setCourseForm({ ...courseForm, credits: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Precio"
                  type="number"
                  value={courseForm.price}
                  onChange={(e) => setCourseForm({ ...courseForm, price: parseFloat(e.target.value) })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowCourseDialog(false); setEditingCourse(null); }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveCourse} disabled={!courseForm.title || guardando}>
            {guardando ? 'Guardando…' : (editingCourse ? 'Guardar' : 'Crear')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para agregar unidad */}
      <Dialog open={showUnitDialog} onClose={() => setShowUnitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Unidad</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Título de la unidad"
              value={unitForm.title}
              onChange={(e) => setUnitForm({ ...unitForm, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Descripción"
              value={unitForm.description}
              onChange={(e) => setUnitForm({ ...unitForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnitDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddUnit} disabled={!unitForm.title}>Agregar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para agregar contenido */}
      <Dialog open={showContentDialog} onClose={() => setShowContentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Contenido</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Título"
              value={contentForm.title}
              onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Descripción"
              value={contentForm.description}
              onChange={(e) => setContentForm({ ...contentForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de contenido</InputLabel>
                  <Select
                    value={contentForm.type}
                    label="Tipo de contenido"
                    onChange={(e) => setContentForm({ ...contentForm, type: e.target.value })}
                  >
                    <MenuItem value="material">📖 Material teórico</MenuItem>
                    <MenuItem value="video">🎬 Video clase</MenuItem>
                    <MenuItem value="quiz">📝 Cuestionario</MenuItem>
                    <MenuItem value="activity">📤 Actividad</MenuItem>
                    <MenuItem value="exam">🎓 Examen</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Puntos"
                  type="number"
                  value={contentForm.points}
                  onChange={(e) => setContentForm({ ...contentForm, points: parseInt(e.target.value) || 0 })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContentDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddContent} disabled={!contentForm.title}>Agregar</Button>
        </DialogActions>
      </Dialog>

      {/* ───────── Dialog EDITAR UNIDAD ───────── */}
      <Dialog
        open={showEditUnitDialog}
        onClose={() => !guardandoUnidad && setShowEditUnitDialog(false)}
        maxWidth="sm" fullWidth
      >
        <DialogTitle>Editar Unidad</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {errorEditando && <Alert severity="error" onClose={() => setErrorEditando(null)}>{errorEditando}</Alert>}
            <TextField
              label="Título de la unidad"
              value={editUnitForm.title}
              onChange={(e) => setEditUnitForm({ ...editUnitForm, title: e.target.value })}
              fullWidth autoFocus
            />
            <TextField
              label="Descripción"
              value={editUnitForm.description}
              onChange={(e) => setEditUnitForm({ ...editUnitForm, description: e.target.value })}
              fullWidth multiline minRows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditUnitDialog(false)} disabled={guardandoUnidad}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveEditUnit}
            disabled={guardandoUnidad || !editUnitForm.title.trim()}
          >
            {guardandoUnidad ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───────── Dialog EDITAR CONTENIDO ───────── */}
      <Dialog
        open={showEditContentDialog}
        onClose={() => !guardandoContenido && setShowEditContentDialog(false)}
        maxWidth="md" fullWidth
      >
        <DialogTitle>
          Editar {editingContent?.type || 'contenido'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {errorEditando && <Alert severity="error" onClose={() => setErrorEditando(null)}>{errorEditando}</Alert>}

            <TextField
              label="Título"
              value={editContentForm.title}
              onChange={(e) => setEditContentForm({ ...editContentForm, title: e.target.value })}
              fullWidth autoFocus
            />
            <TextField
              label="Descripción"
              value={editContentForm.description}
              onChange={(e) => setEditContentForm({ ...editContentForm, description: e.target.value })}
              fullWidth multiline minRows={2}
            />

            {/* Contenido rico para Material */}
            {editingContent?.type === 'Material' && (
              <RichTextEditor
                label="Contenido del material"
                value={editContentForm.bodyText}
                onChange={(html) => setEditContentForm({ ...editContentForm, bodyText: html })}
                minHeight={320}
                placeholder="Escribe el contenido del material..."
                helperText="Encabezados, negrita, cursiva, listas, alineación, enlaces, citas, bloques de código. Botón 🖼️ para insertar imagen (se sube al servidor). Botón ▶ para embeber video de YouTube/Vimeo."
                contenidoId={editingContent?.contentId}
              />
            )}

            {/* URL para Video o SesionGrabada */}
            {(editingContent?.type === 'Video' || editingContent?.type === 'SesionGrabada') && (
              <TextField
                label="URL del recurso"
                value={editContentForm.resourceUrl}
                onChange={(e) => setEditContentForm({ ...editContentForm, resourceUrl: e.target.value })}
                fullWidth
                placeholder="https://..."
              />
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Puntos totales"
                type="number"
                value={editContentForm.maxPoints}
                onChange={(e) => setEditContentForm({ ...editContentForm, maxPoints: e.target.value })}
                sx={{ flex: 1, minWidth: 150 }}
              />
              <TextField
                label="Duración (min)"
                type="number"
                value={editContentForm.durationMinutes}
                onChange={(e) => setEditContentForm({ ...editContentForm, durationMinutes: e.target.value })}
                sx={{ flex: 1, minWidth: 150 }}
              />
            </Box>

            {/* Umbral e intentos: solo para Quiz / Examen */}
            {(editingContent?.type === 'Quiz' || editingContent?.type === 'Examen') && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Umbral de aprobación (%)"
                  type="number"
                  value={editContentForm.minScore}
                  onChange={(e) => setEditContentForm({ ...editContentForm, minScore: e.target.value })}
                  sx={{ flex: 1, minWidth: 180 }}
                />
                <TextField
                  label="Máximo de intentos"
                  type="number"
                  value={editContentForm.maxAttempts}
                  onChange={(e) => setEditContentForm({ ...editContentForm, maxAttempts: e.target.value })}
                  sx={{ flex: 1, minWidth: 180 }}
                />
              </Box>
            )}

            {/* Editor de preguntas: solo para Quiz / Examen */}
            {(editingContent?.type === 'Quiz' || editingContent?.type === 'Examen') && editingContent?.contentId && (
              <PreguntasEditor
                contenidoId={editingContent.contentId}
                kind={editingContent.type}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditContentDialog(false)} disabled={guardandoContenido}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveEditContent}
            disabled={guardandoContenido || !editContentForm.title.trim()}
          >
            {guardandoContenido ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
// ============ TAB CALIFICACIONES PENDIENTES ============
// REESCRITO — usa entregaService.getPendientesInstructor() real. El
// backend ahora incluye un bypass: si el usuario es Administrador
// global, ve TODAS las entregas pendientes de la red (antes solo
// veía las de cursos donde él mismo fuera instructor, casi siempre
// vacío para un admin). Ver AprendizajeController.GetPendientesInstructor.
function GradingTab() {
  const [pendientes, setPendientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [gradeDialog, setGradeDialog] = useState(null);
  const [gradeValue, setGradeValue] = useState('');
  const [feedbackValue, setFeedbackValue] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = () => {
    setCargando(true);
    entregaService.getPendientesInstructor()
      .then(setPendientes)
      .catch((e) => setError(e?.message || 'No se pudieron cargar las entregas pendientes.'))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  const handleGrade = async () => {
    if (!gradeDialog || !gradeValue) return;
    setGuardando(true);
    try {
      await entregaService.calificar(gradeDialog.id, parseInt(gradeValue, 10), feedbackValue, null);
      setGradeDialog(null);
      setGradeValue('');
      setFeedbackValue('');
      cargar();
    } catch (e) {
      alert(e?.message || 'No se pudo guardar la calificación.');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Entregas Pendientes de Calificar ({pendientes.length})
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {pendientes.length > 0 ? (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Estudiante</TableCell>
                <TableCell>Curso</TableCell>
                <TableCell>Actividad</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Archivos</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendientes.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>{item.studentName?.[0]}</Avatar>
                      {item.studentName}
                    </Box>
                  </TableCell>
                  <TableCell>{item.courseTitle}</TableCell>
                  <TableCell>{item.contentTitle}</TableCell>
                  <TableCell>{new Date(item.submittedAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {item.files?.map((f) => (
                      <Chip key={f.id} label={f.fileName} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="contained" onClick={() => setGradeDialog(item)}>
                      Calificar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Alert severity="success" icon={<CheckCircle />}>
          No hay entregas pendientes de calificar
        </Alert>
      )}

      <Dialog open={!!gradeDialog} onClose={() => setGradeDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Calificar Entrega</DialogTitle>
        <DialogContent>
          {gradeDialog && (
            <Box sx={{ pt: 1 }}>
              <Typography gutterBottom><strong>Estudiante:</strong> {gradeDialog.studentName}</Typography>
              <Typography gutterBottom><strong>Actividad:</strong> {gradeDialog.contentTitle}</Typography>
              {gradeDialog.studentComment && (
                <Typography gutterBottom color="text.secondary">
                  <strong>Comentario:</strong> {gradeDialog.studentComment}
                </Typography>
              )}
              <TextField
                label="Calificación (0-100)" type="number" value={gradeValue}
                onChange={(e) => setGradeValue(e.target.value)} fullWidth sx={{ mb: 2, mt: 2 }}
                inputProps={{ min: 0, max: 100 }}
              />
              <TextField
                label="Retroalimentación (opcional)" value={feedbackValue}
                onChange={(e) => setFeedbackValue(e.target.value)} fullWidth multiline rows={3}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGradeDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGrade} disabled={!gradeValue || guardando}>
            {guardando ? 'Guardando…' : 'Guardar Calificación'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============ TAB USUARIOS Y ROLES ============
// Gestión real de roles GLOBALES de EducaT (Administrador/Instructor/
// Estudiante) -- calcado del patrón de "Usuarios y roles" que ya existe
// en News (news.fenagro-oficial.org/admin). Reemplaza el atajo anterior
// de "Ascender a Instructor" con una tabla completa: buscar cualquier
// usuario de la red, ver su rol actual, y asignarle cualquiera de los
// 3 roles reales -- no solo Instructor.
function RolesTab() {
  const nombreProducto = useTheme().productoInfo?.nombre || 'EducaT';
  const [usuarios, setUsuarios] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [dialogUsuario, setDialogUsuario] = useState(null);
  const [nuevoRol, setNuevoRol] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);

  const ROLES = [
    { value: 'Administrador', label: 'Administrador', desc: 'Acceso total al panel /admin de toda la plataforma.' },
    { value: 'Instructor', label: 'Instructor', desc: 'Puede crear cursos, calificar y gestionar sus estudiantes.' },
    { value: 'Estudiante', label: 'Estudiante', desc: 'Rol por defecto -- toma cursos, sin permisos administrativos.' },
  ];

  const ROLE_COLOR = { admin: 'error', instructor: 'primary', student: 'default' };
  const ROLE_LABEL = { admin: 'Administrador', instructor: 'Instructor', student: 'Estudiante' };

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const r = await adminService.getUsuarios({ q: searchQuery || undefined, limit: 50 });
      setUsuarios(r.data);
      setTotal(r.total);
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar los usuarios.');
    } finally {
      setCargando(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const t = setTimeout(cargar, 300); // debounce de la búsqueda
    return () => clearTimeout(t);
  }, [cargar]);

  const abrirAsignar = (usuario) => {
    setDialogUsuario(usuario);
    setNuevoRol(usuario.roleBackend || 'Estudiante');
    setGuardadoOk(false);
  };

  const handleGuardarRol = async () => {
    if (!dialogUsuario || !nuevoRol) return;
    setGuardando(true);
    try {
      await adminService.cambiarRol(dialogUsuario.id, nuevoRol);
      setGuardadoOk(true);
      await cargar();
      setTimeout(() => setDialogUsuario(null), 900);
    } catch (e) {
      alert(e?.message || 'No se pudo cambiar el rol.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>Usuarios y roles</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Rol global de {nombreProducto} -- decide qué puede hacer un usuario en toda la plataforma.
        No confundir con el rol dentro de una institución (Rector, Coordinador...), que se
        administra desde "Mi institución".
      </Typography>

      <TextField
        fullWidth placeholder="Buscar por nombre o email..." value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)} size="small" sx={{ mb: 3 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Último acceso</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios.length > 0 ? usuarios.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={u.avatar} sx={{ width: 32, height: 32 }}>{u.name?.[0] || '?'}</Avatar>
                      {u.name || '—'}
                    </Box>
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip label={ROLE_LABEL[u.role] || u.roleBackend} size="small" color={ROLE_COLOR[u.role] || 'default'} />
                  </TableCell>
                  <TableCell>{u.lastAccess ? new Date(u.lastAccess).toLocaleDateString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => abrirAsignar(u)}>
                      Asignar rol
                    </Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No hay usuarios que coincidan</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
      {!cargando && usuarios.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Mostrando {usuarios.length} de {total} usuario(s).
        </Typography>
      )}

      <Dialog open={!!dialogUsuario} onClose={() => setDialogUsuario(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar rol</DialogTitle>
        <DialogContent>
          {dialogUsuario && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Avatar src={dialogUsuario.avatar}>{dialogUsuario.name?.[0]}</Avatar>
                <Box>
                  <Typography fontWeight={600}>{dialogUsuario.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{dialogUsuario.email}</Typography>
                </Box>
              </Box>

              {guardadoOk && <Alert severity="success" sx={{ mb: 2 }}>Rol actualizado.</Alert>}

              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select value={nuevoRol} label="Rol" onChange={(e) => setNuevoRol(e.target.value)}>
                  {ROLES.map((r) => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {ROLES.find((r) => r.value === nuevoRol)?.desc}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogUsuario(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardarRol} disabled={guardando || !nuevoRol}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============ TAB: CATEGORIAS ============
// CRUD de las categorías de cursos (los pills de filtro de la vista
// 'Explora nuestros cursos'). Solo accesible a Administrador global
// — el backend lo exige con RequireEducaTGlobalAdminFilter.
function CategoriesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [form, setForm] = useState({ nombre: '', color: '#3b82f6', orden: 0, activa: true });

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await categoriaAdminService.getAll();
      setList(data || []);
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar las categorías');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openNew = () => {
    setEditing({ nueva: true });
    const maxOrden = list.reduce((m, c) => Math.max(m, c.orden || 0), 0);
    setForm({ nombre: '', color: '#3b82f6', orden: maxOrden + 10, activa: true });
  };
  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      nombre: cat.nombre || '',
      descripcion: cat.descripcion || '',
      icono: cat.icono || '',
      color: cat.color || '#3b82f6',
      orden: cat.orden || 0,
      activa: cat.activa !== false,
    });
  };

  const saveEdit = async () => {
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSavingEdit(true); setError('');
    try {
      if (editing?.nueva) await categoriaAdminService.create(form);
      else await categoriaAdminService.update(editing.id, form);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e?.message || 'No se pudo guardar');
    } finally { setSavingEdit(false); }
  };

  const moveUp = async (cat, idx) => {
    if (idx === 0) return;
    const prev = list[idx - 1];
    try {
      await categoriaAdminService.reordenar([
        { id: cat.id,  orden: prev.orden },
        { id: prev.id, orden: cat.orden },
      ]);
      await refresh();
    } catch (e) { setError(e?.message || 'No se pudo reordenar'); }
  };
  const moveDown = async (cat, idx) => {
    if (idx === list.length - 1) return;
    const next = list[idx + 1];
    try {
      await categoriaAdminService.reordenar([
        { id: cat.id,  orden: next.orden },
        { id: next.id, orden: cat.orden },
      ]);
      await refresh();
    } catch (e) { setError(e?.message || 'No se pudo reordenar'); }
  };

  const desactivar = async (cat) => {
    if (!window.confirm(`¿Desactivar "${cat.nombre}"? Los cursos vinculados no se tocan, pero la categoría dejará de aparecer en los filtros.`)) return;
    try { await categoriaAdminService.eliminar(cat.id); await refresh(); }
    catch (e) { setError(e?.message || 'No se pudo desactivar'); }
  };
  const eliminarDefinitivo = async (cat) => {
    if (!window.confirm(`¿ELIMINAR DEFINITIVAMENTE "${cat.nombre}"? Esto solo funciona si no tiene cursos vinculados.`)) return;
    try { await categoriaAdminService.eliminarDefinitivo(cat.id); await refresh(); }
    catch (e) { setError(e?.message || 'No se pudo eliminar'); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Categorías de cursos ({list.length})</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openNew}>Nueva categoría</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 90 }}>Orden</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Cursos</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((cat, idx) => (
              <TableRow key={cat.id} hover sx={{ opacity: cat.activa ? 1 : 0.5 }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => moveUp(cat, idx)} disabled={idx === 0}>▲</IconButton>
                    <Typography variant="body2">{cat.orden}</Typography>
                    <IconButton size="small" onClick={() => moveDown(cat, idx)} disabled={idx === list.length - 1}>▼</IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={500}>{cat.nombre}</Typography>
                  {cat.descripcion && <Typography variant="caption" color="text.secondary">{cat.descripcion}</Typography>}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: cat.color || '#ccc', border: '1px solid rgba(0,0,0,.1)' }} />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cat.color || '—'}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{cat.cursosCount || 0}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={cat.activa ? 'Activa' : 'Inactiva'}
                    color={cat.activa ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<Edit />} onClick={() => openEdit(cat)}>Editar</Button>
                  {cat.activa ? (
                    <Button size="small" color="warning" onClick={() => desactivar(cat)}>Desactivar</Button>
                  ) : (
                    <Button size="small" color="error" onClick={() => eliminarDefinitivo(cat)}>
                      Eliminar def.
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No hay categorías. Crea la primera con el botón arriba.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog crear/editar */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.nueva ? 'Nueva categoría' : 'Editar categoría'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              fullWidth required autoFocus
            />
            <TextField
              label="Descripción (opcional)"
              value={form.descripcion || ''}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              fullWidth multiline rows={2}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Color (hex)"
                value={form.color || ''}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: form.color || '#ccc', mr: 1, border: '1px solid rgba(0,0,0,.2)' }} />
                  ),
                }}
                helperText="Ej. #3b82f6"
              />
              <TextField
                label="Orden"
                type="number"
                value={form.orden ?? 0}
                onChange={(e) => setForm({ ...form, orden: parseInt(e.target.value) || 0 })}
                sx={{ width: 120 }}
                helperText="Menor = primero"
              />
            </Box>
            <TextField
              label="Icono (opcional)"
              value={form.icono || ''}
              onChange={(e) => setForm({ ...form, icono: e.target.value })}
              fullWidth
              helperText="Nombre del icono MUI (opcional)"
            />
            <FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="checkbox"
                  checked={!!form.activa}
                  onChange={(e) => setForm({ ...form, activa: e.target.checked })}
                  id="chk-activa"
                />
                <label htmlFor="chk-activa">Activa (visible en los filtros)</label>
              </Box>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveEdit} disabled={savingEdit || !form.nombre.trim()}>
            {savingEdit ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============ COMPONENTE PRINCIPAL ============
function AdminDashboard() {
  const [tab, setTab] = useState(0);
  const { user } = useAuth();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Panel de Administración
        </Typography>
        <Typography color="text.secondary">
          Bienvenido, {user?.name}. Gestiona estudiantes, instructores, cursos y calificaciones.
        </Typography>
      </Box>

      {/* Stats */}
      <StatsCards />

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<People />} label="Estudiantes" iconPosition="start" />
          <Tab icon={<School />} label="Instructores" iconPosition="start" />
          <Tab icon={<MenuBook />} label="Cursos" iconPosition="start" />
          <Tab icon={<Category />} label="Categorías" iconPosition="start" />
          <Tab icon={<Assignment />} label="Calificaciones" iconPosition="start" />
          <Tab icon={<AdminIcon />} label="Usuarios y Roles" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {tab === 0 && <StudentsTab />}
        {tab === 1 && <InstructorsTab />}
        {tab === 2 && <CoursesTab />}
        {tab === 3 && <CategoriesTab />}
        {tab === 4 && <GradingTab />}
        {tab === 5 && <RolesTab />}
      </Box>
    </Container>
  );
}

export default AdminDashboard;
